const validationReporter = require('../../../../helpers/validationReporter').validationReporter;
const Blockly = require('blockly');
const dataAccessMethods = require('../../../../dataAccessMethods');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const addForActionDropdown = require('../../../../helpers/addForActionDropdown');
const createListener = require('../../../../helpers/createListener');
const mdcConfig = require('@sstdev/lib_metadata-config');
const { config } = mdcConfig;

const TOOLTIP = 'Generic toggle for use outside forms for more global actions';
let hasNsRelBlocks = {};
/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;
    const { BLOCK_CHANGE, BLOCK_CREATE, BLOCK_DELETE, FINISHED_LOADING } = Blockly.Events;
    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);
    line1OfInputs.appendField('  Property Name').appendField(new Blockly.FieldTextInput(), 'propertyName');

    const toggleStaticDefaultValueVisibilityAsNecessary = event => {
        if (event.blockId === blockId) {
            if ([BLOCK_CHANGE, BLOCK_CREATE].includes(event.type)) {
                const topInput = workspace.getBlockById(event.blockId).getInput('topInput');
                const staticDefaultVisible = topInput?.fieldRow.some(field => field.name === 'defaultValue');
                if (event.name === 'defaultValueSelector') {
                    if (event.newValue !== 'static' && staticDefaultVisible) {
                        topInput?.removeField('defaultValue');
                    } else if ((event.type === BLOCK_CREATE || event.newValue === 'static') && !staticDefaultVisible) {
                        topInput?.appendField(new Blockly.FieldTextInput('false'), 'defaultValue');
                    }
                } else if (event.type === BLOCK_CREATE && !staticDefaultVisible) {
                    // default selector is static so make default value visible
                    topInput?.appendField(new Blockly.FieldTextInput('false'), 'defaultValue');
                }
            } else if (BLOCK_DELETE === event.type) {
                workspace.removeChangeListener(toggleStaticDefaultValueVisibilityAsNecessary);
            }
        }
        if (event.type === FINISHED_LOADING) {
            const block = workspace.getBlockById(blockId);
            if (block == null) return;
            const defaultValueSelector = block.getFieldValue('defaultValueSelector');
            if (defaultValueSelector !== 'static') {
                const topInput = block.getInput('topInput');
                const staticDefaultVisible = topInput?.fieldRow.some(field => field.name === 'defaultValue') ?? false;
                if (staticDefaultVisible) {
                    topInput?.removeField('defaultValue');
                }
            }
        }
    };
    createListener(workspace, blocklyBlock, toggleStaticDefaultValueVisibilityAsNecessary);

    const defaultValueSelectors = dataAccessMethods.getDefaultValueSelectors();
    line1OfInputs
        .appendField('  Negative (false) Option:')
        .appendField(new Blockly.FieldTextInput(''), 'toggleOption1')
        .appendField('  Positive (true) Option:')
        .appendField(new Blockly.FieldTextInput('true'), 'toggleOption2')
        .appendField('  Default Value:')
        .appendField(new Blockly.FieldDropdown(defaultValueSelectors), 'defaultValueSelector')
        .appendField(new Blockly.FieldTextInput('false'), 'defaultValue');

    const scopeDropdown = new Blockly.FieldDropdown(config.dataRetention.scopeOptions, null);
    scopeDropdown.setValue('global');
    const durationDropdown = new Blockly.FieldDropdown(config.dataRetention.durationOptions, newValue => {
        if (newValue === 'session') {
            return 'pageLoad';
        }
        return newValue;
    });
    const visibilityDropdown = new Blockly.FieldDropdown(config.dataRetention.visibilityOptions);
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Selection Scope:')
        .appendField(scopeDropdown, 'scope')
        .appendField('  Selection Duration:')
        .appendField(durationDropdown, 'duration')
        .appendField('  Selection Visibility:')
        .appendField(visibilityDropdown, 'visibility');

    const hasNsRelBlocksKey = blockId + '|' + workspace.id;
    const addNsRelDropdowns = block => {
        hasNsRelBlocks[hasNsRelBlocksKey] = true;
        // Load possible actions and default action
        const actionsDropdown = new Blockly.FieldDropdown(config.actionElementActions);
        actionsDropdown.setValue('new');
        const nsRelInput = block
            .appendDummyInput('namespaceRelation')
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Namespace:')
            .appendField(
                new Blockly.FieldDropdown(() => {
                    const ns = dataAccessMethods.getNamespaces();
                    ns.unshift(['Nothing', '']);
                    return ns;
                }),
                'namespace'
            )
            .appendField('  Relation:')
            .appendField(
                new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                'relation'
            );
        addForActionDropdown(block, nsRelInput);
        nsRelInput.appendField('  (optional) Subtype:').appendField(new Blockly.FieldTextInput(), 'subtype');
    };
    const toggleNsRelVisibilityAsNecessary = event => {
        if (event.blockId === blockId) {
            if ([BLOCK_CHANGE].includes(event.type)) {
                if (event.name === 'visibility') {
                    // For some reason, the original blocklyBlock reference often
                    // has problems - missing workspace, etc.  So, we get the block
                    // reference again.
                    const block = workspace.getBlockById(blockId);
                    if (event.newValue !== 'perBrowser' && !hasNsRelBlocks[hasNsRelBlocksKey]) {
                        addNsRelDropdowns(block);
                    } else if (event.newValue === 'perBrowser' && hasNsRelBlocks[hasNsRelBlocksKey]) {
                        hasNsRelBlocks[hasNsRelBlocksKey] = false;
                        if (block.getInput('namespaceRelation') != null) {
                            block.removeInput('namespaceRelation');
                        }
                    }
                }
            } else if (BLOCK_CREATE === event.type) {
                const block = workspace.getBlockById(blockId);
                if (block.getInput('namespaceRelation') != null && block.getFieldValue('visibility') !== 'perBrowser') {
                    hasNsRelBlocks[hasNsRelBlocksKey] = false;
                    block.removeInput('namespaceRelation');
                }
            } else if (BLOCK_DELETE === event.type) {
                hasNsRelBlocks[hasNsRelBlocksKey] = false;
                workspace.removeChangeListener(toggleNsRelVisibilityAsNecessary);
            }
        }
        if (FINISHED_LOADING === event.type) {
            // For some reason, blockly doesn't have an input in the block inputs
            // (even if it is visible on the UI) unless it is added at the beginning.
            // Therefore, it won't create code for the block unless we start with it
            // Here, we remove it if it isn't needed.
            const block = workspace.getBlockById(blockId);
            if (block == null) return;
            const visibility = block.getFieldValue('visibility');
            if (visibility === 'perBrowser' && hasNsRelBlocks[hasNsRelBlocksKey]) {
                hasNsRelBlocks[hasNsRelBlocksKey] = false;
                if (block.getInput('namespaceRelation') != null) {
                    block.removeInput('namespaceRelation');
                }
            }
        }
    };
    createListener(workspace, blocklyBlock, toggleNsRelVisibilityAsNecessary);
    addNsRelDropdowns(blocklyBlock);
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation', 'forAction', 'subtype']);

    //action
    addCodeProperties(['propertyName', 'defaultValueSelector']);
    const opt1 = blocklyBlock.getFieldValue('toggleOption1');
    const opt2 = blocklyBlock.getFieldValue('toggleOption2');
    let defaultValue = blocklyBlock.getFieldValue('defaultValue');
    switch (defaultValue) {
        case undefined:
        case null:
        case 'false': // default value for block's field control (must be string)
        case opt1:
            defaultValue = false;
            break;
        case 'true':
        case opt2:
            defaultValue = true;
            break;
        default:
            validationReporter(
                `The default value of ${defaultValue} is not valid.  Use true, false or nothing.`,
                blocklyBlock.id
            );
    }
    addCodeProperties([
        { key: 'toggleValues', handler: () => [opt1, opt2] },
        { key: 'defaultValue', handler: () => defaultValue }
    ]);
    code.retention = {
        duration: blocklyBlock.getFieldValue('duration'),
        scope: blocklyBlock.getFieldValue('scope'),
        visibility: blocklyBlock.getFieldValue('visibility'),
        // Make it easier to pass retention options for consumers of hNode.retention
        // by adding the id for them.
        id: code.id
    };
    return code;
}
module.exports = { addBlockSpecificFields, convertCustomFieldsToCode };
